import { useEffect, useState } from 'react';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { MODAL_SIZES } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import { Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation';
import { BRAND_IMAGES, getBrandImageUrl } from '@/app/common/contentHubAssets.ts';
import useCCMShown from '@/app/common/hooks/useCCMShown';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { getCookie, setCookie } from '@/app/common/cookies/clientCookies';
import { COOKIE_KEY } from '@/app/common/cookies/types';
import { ModalBody, ModalButtons, ModalImage, ModalPrimaryButton, ModalTitle } from './styled';
import type { Localization } from '../../../../common/Localization';
import OpenXModal from '../../OpenXModal';

export type TaxesIncludedModalProps = {
  localization: Localization;
  onModalClose?: () => void;
};

const TaxesIncludedModal = ({ localization, onModalClose }: TaxesIncludedModalProps) => {
  const { t } = useTranslation();
  const { isMobile } = useCommonClient();
  const { commonData } = usePageWrapperData();

  const [showModal, setShowModal] = useState<boolean>(false);
  const isCCMShown = useCCMShown(commonData?.isLocal);

  const modalTitle = t('vacation.oss.taxes_included.modal.title');
  const modalDesc1 = t('vacation.oss.taxes_included.modal.desc');
  const modalDesc2 = t('vacation.oss.taxes_included.modal.desc2');
  const modalCta = t('vacation.oss.taxes_included.modal.cta');

  const image = getBrandImageUrl(BRAND_IMAGES.DEPOSIT, localization);

  // Show taxes included modal only after CCM has been shown (regardless of acceptance) and it hasn't been shown yet
  useEffect(() => {
    if (!isCCMShown) return;

    const hasTaxesIncludedV3Cookie = getCookie(COOKIE_KEY.TAXES_INCLUDED_V3_POPUP);
    if (!hasTaxesIncludedV3Cookie) {
      handleShowModal();
    }
  }, [isCCMShown]);

  const handleShowModal = () => {
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setCookie(COOKIE_KEY.TAXES_INCLUDED_V3_POPUP, 'true');
  };

  const handleCloseModal = () => {
    closeModal();
    onModalClose?.();
  };

  const handleConfirm = () => {
    closeModal();
    onModalClose?.();
  };

  return (
    <OpenXModal
      isOpen={showModal}
      onClose={handleCloseModal}
      className="taxes-included-modal"
      size={MODAL_SIZES.S}
      id="taxes_included_v3_modal"
      aria-label={modalTitle}
      style={{ textAlign: 'center' }}
      isMobile={isMobile}
      brand={localization.brand}
      header={{
        sticky: false,
      }}
      footer={{
        sticky: true,
        element: (
          <ModalButtons>
            <ModalPrimaryButton onClick={handleConfirm}>{modalCta}</ModalPrimaryButton>
          </ModalButtons>
        ),
      }}
    >
      <ModalImage src={image} alt={modalTitle} />
      <ModalTitle>{modalTitle}</ModalTitle>
      <ModalBody>
        <Text variant="BodySmall">{modalDesc1}</Text>
        <Text variant="BodySmall">{modalDesc2}</Text>
      </ModalBody>
    </OpenXModal>
  );
};

export default TaxesIncludedModal;
